/**
 * Phase 34 Session 34.3b — Dashboard TelemetryProvider.
 *
 * Wraps `<App />` so:
 *   - A singleton TelemetryClient instantiates once at first mount.
 *   - The actor + clientSessionId stays in sync with `useAuthStore`.
 *   - Every `useLocation()` change auto-fires `client.pageView(pathname)`
 *     (debounced 200ms so a rapid double-nav doesn't double-emit).
 *   - `visibilitychange === 'hidden'` triggers `flush({beacon: true})` so
 *     a tab close doesn't drop the last batch.
 *   - The `useTrack()` hook + `useTelemetry()` accessor expose the client
 *     to the rest of the app via React Context.
 *
 * Transport wraps the dashboard's existing axios singleton so JWT auth +
 * 401 refresh + `baseURL: /api/v1` all ride along for free.
 *
 * The provider stays mounted across login + logout — `useAuthStore`
 * subscriptions update the actor without remounting the SDK. On logout
 * we clear the actor + flush so events captured under one user identity
 * don't bleed into the next session.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';
import { TelemetryClient, type TelemetryEventName } from '@commerce-os/telemetry-client';
import { telemetryApi } from '@commerce-os/api-client';
import { getApiClient, getTokens } from '../lib/axios';
import { useAuthStore } from '../stores/authStore';

interface TelemetryContextValue {
  client: TelemetryClient | null;
}

const TelemetryContext = createContext<TelemetryContextValue>({ client: null });

const PAGE_VIEW_DEBOUNCE_MS = 200;

function generateClientSessionId(): string {
  // Browser-side stable id for the lifetime of the SPA load. Resets on
  // refresh; survives across navigations within the same tab.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cs_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

export function TelemetryProvider({ children }: { children: ReactNode }) {
  const clientRef = useRef<TelemetryClient | null>(null);

  // One-time instantiation. The transport closure captures `getApiClient()`
  // which is itself memoized — same axios instance every dispatch.
  if (clientRef.current === null) {
    clientRef.current = new TelemetryClient({
      transport: async ({ pageViews, uiEvents, beacon }) => {
        // Never emit telemetry without a session. These endpoints are
        // JWT-guarded; firing them from a public page (login / forgot-
        // password / reset-password) 401s. The axios layer already refuses
        // to reload-loop on those 401s, but there's no point generating the
        // failing requests at all — drop the batch cleanly (non-retryable so
        // it isn't re-queued) until the user is authenticated.
        if (!getTokens()?.accessToken) {
          return { ok: false, retryable: false, reason: 'no-session' } as const;
        }
        try {
          const api = getApiClient();
          const tasks: Promise<unknown>[] = [];
          if (pageViews.length > 0) {
            tasks.push(telemetryApi.sendPageViews(api, pageViews));
          }
          if (uiEvents.length > 0) {
            tasks.push(telemetryApi.sendUiEvents(api, uiEvents));
          }
          await Promise.all(tasks);
          return { ok: true } as const;
        } catch (err) {
          // Distinguish retryable (network / 5xx) from non-retryable (4xx).
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          const retryable = !status || status >= 500;
          // Beacon flushes don't get a retry path because the page is closing
          // — drop them either way to avoid storm-on-reload.
          if (beacon) return { ok: false, retryable: false, reason: 'beacon-failed' } as const;
          return {
            ok: false,
            retryable,
            reason: status ? `http-${status}` : 'network-error',
          } as const;
        }
      },
      onError: (err) => {
        // Soft-log; Sentry will pick it up via global handlers if needed.
        // Don't throw — the SDK already swallows.
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[telemetry] transport error', err);
        }
      },
    });
    clientRef.current.setClientSessionId(generateClientSessionId());
  }

  const client = clientRef.current;

  // Sync actor with auth state — fires on login, logout, and user-edit
  // (e.g. profile name change). Resets the session id on logout so the
  // next user gets a fresh stamp.
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const lastUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!client) return;
    if (lastUserIdRef.current !== userId) {
      // On a transition to logged-out, flush the prior user's events
      // before swapping the actor.
      if (lastUserIdRef.current !== null && userId === null) {
        void client.flush();
        client.setClientSessionId(generateClientSessionId());
      }
      client.setActor(userId);
      lastUserIdRef.current = userId;
    }
  }, [client, userId]);

  // Visibility-driven beacon flush + cleanup.
  useEffect(() => {
    if (!client) return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        void client.flush({ beacon: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    // pagehide fires on bfcache restore + final close even when
    // visibilitychange doesn't (e.g. iOS Safari swipe-back).
    const onPageHide = () => void client.flush({ beacon: true });
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [client]);

  const value = useMemo<TelemetryContextValue>(() => ({ client }), [client]);

  return (
    <TelemetryContext.Provider value={value}>
      <RouteListener />
      {children}
    </TelemetryContext.Provider>
  );
}

/**
 * Internal — listens for `useLocation()` changes and fires `pageView()`
 * on every navigation. Debounced 200ms so SPA route flickers (a redirect
 * chain or a quick back-tap) don't double-emit.
 */
function RouteListener() {
  const { client } = useContext(TelemetryContext);
  const location = useLocation();
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!client) return;
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      // Only record page-views for authenticated sessions. Views of the
      // public auth pages can't be attributed to an actor anyway, and
      // emitting them just produces 401s (see the transport gate above).
      if (getTokens()?.accessToken) {
        client.pageView(location.pathname);
      }
      debounceRef.current = null;
    }, PAGE_VIEW_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    // pathname-only — search + hash navigations within the same page
    // shouldn't double-fire as a fresh page-view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, location.pathname]);

  return null;
}

/**
 * Hook returning a stable callback that fires a typed UI event. Safe to
 * call from any depth — does nothing when the provider isn't mounted
 * (e.g. in tests that render a component without the wrapping
 * TelemetryProvider).
 */
export function useTrack(): (
  eventName: TelemetryEventName | string,
  extra?: { context?: Record<string, unknown> | null; route?: string | null },
) => void {
  const { client } = useContext(TelemetryContext);
  return useMemo(() => {
    return (eventName, extra) => {
      if (!client) return;
      client.track(eventName, extra);
    };
  }, [client]);
}

/** Direct-access escape hatch — useful for forcing a synchronous flush
 *  before a hard navigation (e.g. window.location.assign). */
export function useTelemetry(): TelemetryContextValue {
  return useContext(TelemetryContext);
}
